import {
  Card,
  CenteredSpinner,
  FormGroup,
  Icon,
  InformationalLegends,
  Label,
  Row,
  Text,
  Title,
} from '@wyz/ui';
import { Trans } from '~/common/context';
import { FEATURES } from '~/common/helpers';
import { useAccessControl } from '~/common/hooks';
import { useManageParameters, useManageSettingTemplates } from '~/connect/api';
import { CustomerFormProps } from '~/connect/types';

const SettingTemplateForm = ({
  forms,
  internalCriteria,
}: CustomerFormProps) => {
  const acl = useAccessControl();

  const watchedCriteria = forms.CRITERIA_FORM.methods.watch('criteria');

  const hasSelectedCriteria =
    watchedCriteria.length > 0 &&
    watchedCriteria.every(
      (criterion) => criterion.criterion.id && criterion.value,
    );

  const manageSettingTemplates = useManageSettingTemplates({
    getMatched: {
      enabled: hasSelectedCriteria,
      args: {
        customerCriteria: watchedCriteria.map(({ criterion, value }) => ({
          id: criterion.id,
          valueId: value,
        })),
        customerInternalCriteria: internalCriteria,
      },
    },
  });

  const { getReferenceGroups, getPriceFrameTypesQuery } = useManageParameters({
    getReferenceGroups: {
      enabled: true,
    },
    getPriceFrameTypesQuery: {
      enabled: true,
    },
  });
  const isLoading =
    manageSettingTemplates.getMatched.isFetching ||
    getReferenceGroups.isLoading;

  const settingTemplate = manageSettingTemplates.getMatched.data?.data;

  return (
    <>
      <Card>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Trans i18nKey='app.entity.tab.setting' />
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <FormGroup>
              <Label>
                <Trans i18nKey='app.entity.tab.setting_template' />
              </Label>
              <Text heading='normal' weight='semi-bold'>
                {isLoading ? (
                  <Icon icon='spinner' size='xs' />
                ) : (
                  (manageSettingTemplates.getMatched.data?.data?.name ?? '-')
                )}
              </Text>
            </FormGroup>
          </Row>
          <hr />

          <div className='bg-neutral-grey-1 border rounded-3 border-neutral-grey-4 p-3'>
            {(getReferenceGroups.isLoading ||
              getPriceFrameTypesQuery.isLoading) && <CenteredSpinner />}
            {getReferenceGroups.isSuccess &&
              getPriceFrameTypesQuery.isSuccess && (
                <>
                  {settingTemplate ? (
                    <InformationalLegends.Container>
                      <InformationalLegends.Row>
                        <InformationalLegends.Label>
                          <Trans i18nKey='app.setting_template.form.settings.manufacturer_reference' />
                        </InformationalLegends.Label>
                        <InformationalLegends.Value id='reference-group-name-value'>
                          {getReferenceGroups.data?.data.find(
                            (group) =>
                              group.publicKey ===
                              settingTemplate.generalSetting
                                ?.referenceGroupPublicKey,
                          )?.name ?? '-'}
                        </InformationalLegends.Value>
                      </InformationalLegends.Row>
                      <InformationalLegends.Row>
                        <InformationalLegends.Label>
                          <Trans i18nKey='app.setting_template.form.settings.back_order' />
                        </InformationalLegends.Label>
                        <InformationalLegends.Value id='back-order-value'>
                          {settingTemplate.generalSetting?.backOrder ? (
                            <Trans i18nKey='app.common.messages.yes' />
                          ) : (
                            <Trans i18nKey='app.common.messages.no' />
                          )}
                        </InformationalLegends.Value>
                      </InformationalLegends.Row>
                      <InformationalLegends.Row>
                        <InformationalLegends.Label>
                          <Trans i18nKey='app.setting_template.form.settings.alternative_reference_enabled' />
                        </InformationalLegends.Label>
                        <InformationalLegends.Value id='alternative-reference-enabled-value'>
                          {settingTemplate.generalSetting
                            ?.alternativeReferenceEnabled ? (
                            <Trans i18nKey='app.common.messages.yes' />
                          ) : (
                            <Trans i18nKey='app.common.messages.no' />
                          )}
                        </InformationalLegends.Value>
                      </InformationalLegends.Row>
                      <InformationalLegends.Row>
                        <InformationalLegends.Label>
                          <Trans i18nKey='app.setting_template.form.settings.best_price' />
                        </InformationalLegends.Label>
                        <InformationalLegends.Value id='best-price-value'>
                          {settingTemplate.alternativeOfferSetting
                            ?.bestPrice ? (
                            <Trans i18nKey='app.common.messages.yes' />
                          ) : (
                            <Trans i18nKey='app.common.messages.no' />
                          )}
                        </InformationalLegends.Value>
                      </InformationalLegends.Row>
                      <InformationalLegends.Row>
                        <InformationalLegends.Label>
                          <Trans i18nKey='app.setting_template.form.settings.best_delivery' />
                        </InformationalLegends.Label>
                        <InformationalLegends.Value id='best-delivery-value'>
                          {settingTemplate.alternativeOfferSetting
                            ?.bestDelay ? (
                            <Trans i18nKey='app.common.messages.yes' />
                          ) : (
                            <Trans i18nKey='app.common.messages.no' />
                          )}
                        </InformationalLegends.Value>
                      </InformationalLegends.Row>
                      <InformationalLegends.Row>
                        <InformationalLegends.Label>
                          <Trans i18nKey='app.setting_template.form.settings.j0' />
                        </InformationalLegends.Label>
                        <InformationalLegends.Value id='j0-value'>
                          {settingTemplate.alternativeOfferSetting?.j0 ? (
                            <Trans i18nKey='app.common.messages.yes' />
                          ) : (
                            <Trans i18nKey='app.common.messages.no' />
                          )}
                        </InformationalLegends.Value>
                      </InformationalLegends.Row>
                      <InformationalLegends.Row>
                        <InformationalLegends.Label>
                          <Trans i18nKey='app.setting_template.form.settings.price_frame_types' />
                        </InformationalLegends.Label>
                        <InformationalLegends.Value id='price-frame-types-value'>
                          {getPriceFrameTypesQuery.data?.find(
                            (option) =>
                              option.value ===
                              settingTemplate.generalSetting
                                ?.alternativeSupplierPriceFrameType,
                          )?.label ?? '-'}
                        </InformationalLegends.Value>
                      </InformationalLegends.Row>
                      {acl.isEnabledFeatures([
                        FEATURES.REDISTRIBUTION,
                        FEATURES.DESTOCKING,
                      ]) && (
                        <InformationalLegends.Row>
                          <InformationalLegends.Label>
                            <Trans i18nKey='app.setting_template.form.destocking.enable_for_model' />
                          </InformationalLegends.Label>
                          <InformationalLegends.Value id='destocking-enabled-value'>
                            {settingTemplate.generalSetting
                              ?.destockingEnabled ? (
                              <Trans i18nKey='app.common.messages.yes' />
                            ) : (
                              <Trans i18nKey='app.common.messages.no' />
                            )}
                          </InformationalLegends.Value>
                        </InformationalLegends.Row>
                      )}
                    </InformationalLegends.Container>
                  ) : (
                    '-'
                  )}
                </>
              )}
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default SettingTemplateForm;
